import {
    BufferGeometry,
    type LineBasicMaterial,
    Matrix4,
    type MeshBasicMaterial,
    type SphereGeometry,
    type Vector2,
} from 'three';
import { markRaw } from 'vue';
import { meshNode } from '@/lib/threeScene/nodes/mesh';
import { lineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import type { LandmarkMap, RayAngleRefs } from '@/domain/anatomy/hipSpineAngleOverlays';
import { HipSpineAngleId } from '@/domain/anatomy/hipSpineAngleOverlays';
import type { AngleNodes, HipSpineAngleResult } from '@/views/3d-planner/hipSpine/types';
import { clampRayToRect, resolveRayGeometry, toWorld, type RayGeometry } from '@/views/3d-planner/hipSpine/angles/angleGeometry';
import { ARC_SEGMENTS, tessellateArc, tessellateDashedLine } from '@/domain/geometry/tessellate';
import { prepareLabelPlacement, type AngleLabelPlacement } from '@/views/3d-planner/hipSpine/angles/angleLabels';

const RAY_LENGTH_FACTOR = 0.2;

export type RayBuildContext = {
    groupId: string;
    landmarkMap: LandmarkMap;
    maxDim: number;
    imageSize: { width: number; height: number };
    sharedGeometry: SphereGeometry;
    meshMaterial: MeshBasicMaterial;
    lineMaterial: LineBasicMaterial;
    sphereRadius: number;
    arcRadius: number;
    dashLength: number;
    gapLength: number;
    colour: string;
    config: { ray1Dashed: boolean; ray2Dashed: boolean };
};

function isPfa(id: string): boolean {
    return id === HipSpineAngleId.PelvicFemoral1 || id === HipSpineAngleId.PelvicFemoral2;
}

function clampPfaRays(angleId: string, geo: RayGeometry, size: { width: number; height: number }): void {
    if (!isPfa(angleId)) return;
    geo.ray1End = clampRayToRect(geo.vertex, geo.ray1End, size.width, size.height);
    geo.ray2End = clampRayToRect(geo.vertex, geo.ray2End, size.width, size.height);
}

function vertexDot(angle: HipSpineAngleResult, geo: RayGeometry, ctx: RayBuildContext): StandardNode {
    return meshNode(`vertex-${angle.id}`, {
        parent: ctx.groupId,
        name: `vertex-${angle.id}`,
        geometry: ctx.sharedGeometry,
        material: ctx.meshMaterial,
        transform: new Matrix4().setPosition(geo.vertex.x, geo.vertex.y, 0),
    });
}

type RaySpec = { name: string; start: Vector2; end: Vector2; dashed: boolean };

function rayLine(spec: RaySpec, ctx: RayBuildContext): StandardNode | null {
    const verts = spec.dashed
        ? tessellateDashedLine(toWorld(spec.start), toWorld(spec.end), ctx.dashLength, ctx.gapLength)
        : [toWorld(spec.start), toWorld(spec.end)];
    if (verts.length < 2) return null;
    return lineSegmentsNode(spec.name, {
        parent: ctx.groupId,
        name: spec.name,
        geometry: markRaw(new BufferGeometry().setFromPoints(verts)),
        material: ctx.lineMaterial,
    });
}

type ArcSweep = {
    ray1Dir: Vector2;
    ray2Dir: Vector2;
    startAngle: number;
    sweepEnd: number;
    isReflex: boolean;
};

function computeArcSweep(geo: RayGeometry, angleValue: number): ArcSweep {
    const ray1Dir = geo.ray1End.clone().sub(geo.vertex).normalize();
    const ray2Dir = geo.ray2End.clone().sub(geo.vertex).normalize();
    const startAngle = Math.atan2(ray1Dir.y, ray1Dir.x);
    const endAngle = Math.atan2(ray2Dir.y, ray2Dir.x);

    let sweepEnd = endAngle;
    const diff = sweepEnd - startAngle;
    if (diff > Math.PI) sweepEnd -= 2 * Math.PI;
    else if (diff < -Math.PI) sweepEnd += 2 * Math.PI;

    // For reflex angles (> 180°), sweep the long way around the rays.
    const isReflex = angleValue > 180;
    if (isReflex) {
        if (sweepEnd > startAngle) sweepEnd -= 2 * Math.PI;
        else sweepEnd += 2 * Math.PI;
    }
    return { ray1Dir, ray2Dir, startAngle, sweepEnd, isReflex };
}

function arcNode(
    angle: HipSpineAngleResult,
    geo: RayGeometry,
    sweep: ArcSweep,
    ctx: RayBuildContext,
): StandardNode | null {
    const arcVerts = tessellateArc(
        {
            center: toWorld(geo.vertex),
            radius: ctx.arcRadius,
            startAngle: sweep.startAngle,
            endAngle: sweep.sweepEnd,
        },
        ARC_SEGMENTS,
    );
    if (arcVerts.length < 2) return null;
    return lineSegmentsNode(`arc-${angle.id}`, {
        parent: ctx.groupId,
        name: `arc-${angle.id}`,
        geometry: markRaw(new BufferGeometry().setFromPoints(arcVerts)),
        material: ctx.lineMaterial,
    });
}

function auxiliaryLandmarkDotNodes(angle: HipSpineAngleResult, geo: RayGeometry, ctx: RayBuildContext): StandardNode[] {
    return geo.extraDots.map((dot, i) =>
        meshNode(`dot-${angle.id}-${i}`, {
            parent: ctx.groupId,
            name: `dot-${angle.id}-${i}`,
            geometry: ctx.sharedGeometry,
            material: ctx.meshMaterial,
            transform: new Matrix4().setPosition(dot.x, dot.y, 0),
        }),
    );
}

function rayLabelPlacement(
    angle: HipSpineAngleResult,
    geo: RayGeometry,
    sweep: ArcSweep,
    ctx: RayBuildContext,
): AngleLabelPlacement | null {
    // Clone so computeArcSweep's stored ray directions are not mutated by the add.
    const bisectorDir = sweep.ray1Dir.clone().add(sweep.ray2Dir).normalize();
    if (sweep.isReflex) bisectorDir.negate();
    const labelPos = geo.vertex.clone().add(bisectorDir.multiplyScalar(ctx.arcRadius * 2.5));
    return prepareLabelPlacement({
        angleId: angle.id,
        groupId: ctx.groupId,
        position: labelPos,
        value: angle.value,
        colour: ctx.colour,
        sphereRadius: ctx.sphereRadius,
    });
}

export function buildRayAngleNodes(
    angle: HipSpineAngleResult,
    refs: RayAngleRefs,
    ctx: RayBuildContext,
): AngleNodes | null {
    const geo = resolveRayGeometry(refs, ctx.landmarkMap, ctx.maxDim, RAY_LENGTH_FACTOR);
    if (!geo) return null;
    clampPfaRays(angle.id, geo, ctx.imageSize);

    const sweep = computeArcSweep(geo, angle.value);
    const children: StandardNode[] = [vertexDot(angle, geo, ctx)];

    const ray1 = rayLine(
        { name: `ray1-${angle.id}`, start: geo.vertex, end: geo.ray1End, dashed: ctx.config.ray1Dashed },
        ctx,
    );
    if (ray1) children.push(ray1);
    const ray2 = rayLine(
        { name: `ray2-${angle.id}`, start: geo.vertex, end: geo.ray2End, dashed: ctx.config.ray2Dashed },
        ctx,
    );
    if (ray2) children.push(ray2);

    const arc = arcNode(angle, geo, sweep, ctx);
    if (arc) children.push(arc);

    children.push(...auxiliaryLandmarkDotNodes(angle, geo, ctx));

    return { children, labelPlacement: rayLabelPlacement(angle, geo, sweep, ctx) };
}
